import { Component, DoCheck, Input, IterableDiffers, OnDestroy } from '@angular/core';
import { ElementAttribute, ElementTagName } from '@models/editor';
import { CanvasInputMode } from '@service/canvas/canvas-input-mode.service';
import { PathEditService } from '@service/canvas/path-editor.service';
import { SelectedElementsService } from '@service/canvas/selected-elements.service';
import { TriangleDrawHelperService } from '@service/canvas/triangle-draw-helper.service';
import { defaultNumberInputArrowedKey, KeyFilterSerivce } from '@service/key-filter.service';
import { UndoManagerService } from '@service/canvas/undoMgr.service';
import { CanvasService } from '@service/canvas/canvas.service';
import { mixedString } from 'icreate/common/models/mimic';
import { ConnectorPlacementService, ConnectorType } from '@service/canvas/connector-placement.service';

@Component({
    selector: 'app-dimensions-options',
    templateUrl: './dimensions-options.component.html',
    styleUrls: ['./../design-side-menu/design-side-menu.component.css'],
    standalone: false,
})
export class DimensionsOptionsComponent implements DoCheck, OnDestroy {
    readonly ALLOWED_KEYS = defaultNumberInputArrowedKey;
    readonly MIXED_STRING = mixedString;

    @Input() selectedElements: any[];
    constrainState: boolean = false;

    width: string;
    height: string;
    thickness: string;

    readonly DIMENSION_W = 'W';
    readonly DIMENSION_H = 'H';

    iterableDiffer;
    observer: MutationObserver;

    constructor(
        private iterableDiffers: IterableDiffers,
        private selectionService: SelectedElementsService,
        private pathService: PathEditService,
        private triangleService: TriangleDrawHelperService,
        private keyFilterService: KeyFilterSerivce,
        private undoManagerService: UndoManagerService,
        private canvasService: CanvasService,
        private connectorPlacementService: ConnectorPlacementService
    ) {
        this.iterableDiffer = iterableDiffers.find([]).create(null);
    }

    ngDoCheck() {
        let changes = this.iterableDiffer.diff(this.selectedElements);
        if (changes) {
            this.updateDimenions();

            if (this.isDefaultLibraryElement && !this.isConnectorElement) {
                this.constrainState = true;
            } else {
                const constrainState = this.selectedElements
                    .map(element => {
                        return Boolean(JSON.parse(element.getAttribute(ElementAttribute.constraint)));
                    })
                    .some(condition => !condition);

                this.constrainState = !constrainState;
            }

            if (this.selectedElements.length === 1) {
                this.observer?.disconnect();
                this.observer = new MutationObserver(() => {
                    this.checkCurrentDimensions();
                    if (this.displayThickness) this.updateThickness();
                });

                const resolved = this.resolveElement(this.selectedElements[0]);
                const attributeFilter = [
                    ElementAttribute.width,
                    ElementAttribute.height,
                    ElementAttribute.cx,
                    ElementAttribute.cy,
                    ElementAttribute.x2,
                    ElementAttribute.y2,
                    ElementAttribute.d,
                ];

                const icreateType = resolved.getAttribute(ElementAttribute.iCreateType);
                if (resolved.tagName === ElementTagName.g) {
                    attributeFilter.push(ElementAttribute.transform);
                }
                if (icreateType == CanvasInputMode.GAUGE) {
                    attributeFilter.push(ElementAttribute.style);
                    const canvas = this.canvasService.getCanvas();
                    canvas.getCanvas().bind('changed', () => {
                        this.checkCurrentDimensions();
                    });
                } else if (this.displayThickness) {
                    this.updateThickness();
                }
                this.observer.observe(resolved, {
                    attributes: true,
                    attributeFilter,
                });
            }
        }
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
    }

    handleThicknessChange({ name, value }) {
        if (!this.displayThickness) return;
        this.connectorPlacementService.setConnectorThickness(this.selectedElements[0], value);
        this.updateThickness();
    }

    updateThickness() {
        const thickness = this.connectorPlacementService.getConnectorThickness(this.selectedElements[0]);
        this.thickness = Math.round(thickness).toString();
    }

    handleDimensionChange({ name, value }) {
        const axis = [this.DIMENSION_W, this.DIMENSION_H].includes(name) ? name : null;
        if (!axis) return;

        const isWidth = axis === this.DIMENSION_W;
        value = Number(value) || 0;

        if (this.selectedElements.length > 0) {
            const first = this.resolveElement(this.selectedElements[0]);

            let currentValue;
            if (first.tagName === ElementTagName.ellipse) {
                const radiusAttr = isWidth ? ElementAttribute.rx : ElementAttribute.ry;
                currentValue = Number(first.getAttribute(radiusAttr)) * 2;
            } else if (first.tagName === ElementTagName.line) {
                const start = Number(first.getAttribute(isWidth ? ElementAttribute.x1 : ElementAttribute.y1));
                const end = Number(first.getAttribute(isWidth ? ElementAttribute.x2 : ElementAttribute.y2));
                currentValue = end - start;
            } else if (first.tagName === ElementTagName.path) {
                const bbox = first.getBBox();
                currentValue = isWidth ? bbox.width : bbox.height;
            } else {
                currentValue = Number(first.getAttribute(isWidth ? ElementAttribute.width : ElementAttribute.height));
            }

            if (currentValue === value) {
                return;
            }
        }

        const mainSize = Number(isWidth ? this.width : this.height);
        const crossSize = Number(isWidth ? this.height : this.width);

        const attrMap = isWidth
            ? {
                  radius: ElementAttribute.rx,
                  crossRadius: ElementAttribute.ry,
                  mainSize: ElementAttribute.width,
                  crossSize: ElementAttribute.height,
                  mainStart: ElementAttribute.x1,
                  mainEnd: ElementAttribute.x2,
                  crossStart: ElementAttribute.y1,
                  crossEnd: ElementAttribute.y2,
              }
            : {
                  radius: ElementAttribute.ry,
                  crossRadius: ElementAttribute.rx,
                  mainSize: ElementAttribute.height,
                  crossSize: ElementAttribute.width,
                  mainStart: ElementAttribute.y1,
                  mainEnd: ElementAttribute.y2,
                  crossStart: ElementAttribute.x1,
                  crossEnd: ElementAttribute.x2,
              };

        value = Number(value) || 0;
        const canvas = this.canvasService.getCanvas();
        const batchCmd = new canvas.history.BatchCommand('Dimension constrain');
        this.selectedElements.forEach(originalElement => {
            const element = this.resolveElement(originalElement);
            const type = element.getAttribute(ElementAttribute.type);
            const newCrossSize = Math.round((value / mainSize) * crossSize);

            const backupAttrs = keys =>
                keys.reduce((acc, key) => {
                    acc[key] = element.getAttribute(key);
                    return acc;
                }, {});

            const handleEllipse = () => {
                const backup = backupAttrs([ElementAttribute.rx, ElementAttribute.ry]);
                batchCmd.addSubCommand(new canvas.history.ChangeElementCommand(element, backup, 'Constrain changed'));
                if (this.constrainState) {
                    element.setAttribute(attrMap.crossRadius, newCrossSize / 2);
                }
                const oldVal = Number(element.getAttribute(attrMap.radius));
                element.setAttribute(attrMap.radius, value / 2);
                if (!this.constrainState) {
                    this.undoManagerService.addCommandToHistory(
                        element,
                        { [attrMap.radius]: oldVal },
                        'Dimensions changed'
                    );
                }
            };

            const handleLine = () => {
                const backup = backupAttrs([
                    ElementAttribute.x1,
                    ElementAttribute.x2,
                    ElementAttribute.y1,
                    ElementAttribute.y2,
                ]);
                batchCmd.addSubCommand(new canvas.history.ChangeElementCommand(element, backup, 'Constrain changed'));
                if (this.constrainState) {
                    const mainStartVal = Number(element.getAttribute(attrMap.crossStart));
                    element.setAttribute(attrMap.crossEnd, mainStartVal + newCrossSize);
                }
                const startVal = Number(element.getAttribute(attrMap.mainStart));
                element.setAttribute(attrMap.mainEnd, startVal + value);
                if (!this.constrainState) {
                    this.undoManagerService.addCommandToHistory(
                        element,
                        { [attrMap.mainEnd]: startVal },
                        'Dimensions changed'
                    );
                }
            };

            const handlePath = () => {
                const backup = backupAttrs(['d', 'transform']);
                const bbox = element.getBBox();
                backup.width = bbox.width;
                backup.height = bbox.height;
                batchCmd.addSubCommand(new canvas.history.ChangeElementCommand(element, backup, 'Constrain changed'));
                this.pathService.scalePath(element, value, this.constrainState ? value : crossSize);
                if (!this.constrainState) {
                    this.undoManagerService.addCommandToHistory(element, backup, 'Path dimensions changed');
                }
            };

            const handleDefault = () => {
                const backup = backupAttrs([attrMap.mainSize, attrMap.crossSize]);
                batchCmd.addSubCommand(new canvas.history.ChangeElementCommand(element, backup, 'Constrain changed'));
                if (this.constrainState) {
                    element.setAttribute(attrMap.crossSize, newCrossSize);
                }
                const oldVal = Number(element.getAttribute(attrMap.mainSize));
                element.setAttribute(attrMap.mainSize, value);
                if (!this.constrainState) {
                    this.undoManagerService.addCommandToHistory(
                        element,
                        { [attrMap.mainSize]: oldVal },
                        'Dimensions changed'
                    );
                }
            };

            const handleGroup = () => {
                const transformList = element.transform?.baseVal;
                const oldTransform = element.getAttribute(ElementAttribute.transform);
                let curScaleX = 1,
                    curScaleY = 1,
                    curTx = 0,
                    curTy = 0;

                if (transformList && transformList.numberOfItems > 0) {
                    const m = transformList.consolidate()?.matrix;
                    if (!m) return;
                    curScaleX = m.a;
                    curScaleY = m.d;
                    curTx = m.e;
                    curTy = m.f;
                    element.removeAttribute(ElementAttribute.transform);
                }

                const naturalBbox = element.getBBox();

                if (oldTransform) {
                    element.setAttribute(ElementAttribute.transform, oldTransform);
                }

                const naturalW = naturalBbox.width;
                const naturalH = naturalBbox.height;
                if (!naturalW || !naturalH) return;

                const naturalCx = naturalBbox.x + naturalW / 2;
                const naturalCy = naturalBbox.y + naturalH / 2;
                const renderedCx = curScaleX * naturalCx + curTx;
                const renderedCy = curScaleY * naturalCy + curTy;
                const newMainScale = value / (isWidth ? naturalW : naturalH);
                const newCrossScale = this.constrainState
                    ? newMainScale * (isWidth ? curScaleY / curScaleX : curScaleX / curScaleY)
                    : isWidth
                      ? curScaleY
                      : curScaleX;
                const newScaleX = isWidth ? newMainScale : newCrossScale;
                const newScaleY = isWidth ? newCrossScale : newMainScale;
                const newTx = renderedCx - newScaleX * naturalCx;
                const newTy = renderedCy - newScaleY * naturalCy;
                this.undoManagerService.addCommandToHistory(element, { transform: oldTransform }, 'Dimensions changed');
                element.setAttribute(
                    ElementAttribute.transform,
                    `matrix(${newScaleX}, 0, 0, ${newScaleY}, ${newTx}, ${newTy})`
                );
            };

            if (!type) {
                if (element.tagName === ElementTagName.ellipse) handleEllipse();
                else if (element.tagName === ElementTagName.line) handleLine();
                else if (element.tagName === ElementTagName.path) handlePath();
                else if (element.getAttribute(ElementAttribute.connectorType) == ConnectorType.DIRECTION) {
                    const currentDimensions = this.connectorPlacementService.getGroupBBoxExcludingIgnored(element);
                    const currentWidth = Math.round(currentDimensions.width);
                    const currentHeight = Math.round(currentDimensions.height);
                    const height = isWidth ? currentHeight : value;
                    const width = isWidth ? value : currentWidth;
                    this.connectorPlacementService.setConnectorSize(element, { width, height });
                } else if (element.tagName === ElementTagName.g) handleGroup();
                else handleDefault();
            } else if (type === CanvasInputMode.TRIANGLE) {
                const backup = backupAttrs(['d']);
                const bbox = element.getBBox();
                backup.width = bbox.width;
                backup.height = bbox.height;
                this.triangleService.changeDimensions(element, value, axis, this.constrainState);
                this.undoManagerService.addCommandToHistory(element, backup, 'Triangle dimensions changed');
            } else if (type === CanvasInputMode.ADD_SVG) {
                handleGroup();
            } else {
                const oldMain = parseInt(element.getAttribute(attrMap.mainSize));
                if (this.constrainState && !this.isJunctionConnector) {
                    const cross = parseInt(element.getAttribute(attrMap.crossSize));
                    const newCross = Math.round((value / oldMain) * cross);
                    element.setAttribute(attrMap.crossSize, `${newCross}px`);
                }
                element.setAttribute(attrMap.mainSize, `${value}px`);
                this.undoManagerService.addCommandToHistory(
                    element,
                    { [attrMap.mainSize]: oldMain },
                    'Dimensions changed'
                );
            }
        });

        if (this.constrainState) {
            canvas.addCommandToHistory(batchCmd);
        }

        this.updateDimenions();

        canvas.call('changed', this.selectedElements);
        this.selectionService.setSelectedElements(this.selectedElements);
    }

    updateDimenions() {
        if (this.selectedElements.length > 1) {
            this.width = mixedString;
            this.height = mixedString;
            return;
        }
        this.checkCurrentDimensions();
    }

    checkCurrentDimensions() {
        const selectedElement = this.resolveElement(this.selectedElements[0]);
        if (
            selectedElement.getAttribute(ElementAttribute.type) &&
            selectedElement.getAttribute(ElementAttribute.type) !== CanvasInputMode.TRIANGLE
        ) {
            const width = selectedElement.getAttribute(ElementAttribute.width);
            this.width = width?.endsWith('px') ? width.slice(0, -2) : width;
            const height = selectedElement.getAttribute(ElementAttribute.height);
            this.height = height?.endsWith('px') ? height.slice(0, -2) : height;
        } else {
            this.width = selectedElement.getAttribute(ElementAttribute.width);
            this.height = selectedElement.getAttribute(ElementAttribute.height);
        }

        if (!this.width || !this.height) {
            if (selectedElement.tagName === ElementTagName.g) {
                const layerElement = selectedElement.parentElement as unknown as SVGGraphicsElement;
                const layerCTM = layerElement?.getScreenCTM?.();
                if (selectedElement.getAttribute(ElementAttribute.connectorType) == ConnectorType.DIRECTION) {
                    const dimensions = this.connectorPlacementService.getGroupBBoxExcludingIgnored(selectedElement);
                    this.width = Math.round(dimensions.width).toString();
                    this.height = Math.round(dimensions.height).toString();
                    return;
                } else if (layerCTM) {
                    const rect = (selectedElement as unknown as Element).getBoundingClientRect();
                    const inv = layerCTM.inverse();
                    const tl = new DOMPoint(rect.left, rect.top).matrixTransform(inv);
                    const br = new DOMPoint(rect.right, rect.bottom).matrixTransform(inv);
                    this.width = String(Math.round(br.x - tl.x));
                    this.height = String(Math.round(br.y - tl.y));
                    return;
                }
            }
            const bbox = (selectedElement as any).getBBox();
            this.width = bbox.width;
            this.height = bbox.height;
        }
    }

    constrainClick() {
        if (this.isConstrainDisabled) return;
        this.constrainState = !this.constrainState;
        this.selectedElements.forEach(element => {
            element.setAttribute(ElementAttribute.constraint, this.constrainState);
        });
    }

    validDimensionKeyDown(eventObj: { event: KeyboardEvent; value }) {
        const { event, value } = eventObj;
        const shouldBlockKey = this.keyFilterService.shouldPreventDefault(event, this.ALLOWED_KEYS);
        if (shouldBlockKey) event.preventDefault();
    }

    private resolveElement(element: any): any {
        if (element?.parentElement?.getAttribute(ElementAttribute.resizable)) {
            return element.parentElement;
        }
        return element;
    }

    get isDefaultLibraryElement(): boolean {
        if (this.selectedElements?.length !== 1) return false;
        const element = this.resolveElement(this.selectedElements[0]);
        if (!element.getAttribute(ElementAttribute.resizable)) return false;
        const type = element.getAttribute(ElementAttribute.type);
        return !type?.startsWith('connector-');
    }

    get isJunctionConnector(): boolean {
        if (this.selectedElements?.length !== 1) return false;
        const element = this.resolveElement(this.selectedElements[0]);
        const connectorType = element.getAttribute(ElementAttribute.connectorType);
        return connectorType === ConnectorType.JUNCTION;
    }

    get isConnectorElement(): boolean {
        if (this.selectedElements?.length !== 1) return false;
        const element = this.resolveElement(this.selectedElements[0]);
        if (!element.getAttribute(ElementAttribute.resizable)) return false;
        const connectorType = element.getAttribute(ElementAttribute.connectorType);
        const connectorIcon = element.getAttribute(ElementAttribute.connectorIcon);
        const isAnyConnector = !!connectorType || !!connectorIcon;
        return isAnyConnector && !this.isJunctionConnector;
    }

    get displayThickness(): boolean {
        if (this.selectedElements?.length !== 1) return false;
        const element = this.resolveElement(this.selectedElements[0]);
        const connectorType = element.getAttribute(ElementAttribute.connectorType);
        if (!connectorType) return false;
        return connectorType === ConnectorType.CORNER || connectorType == ConnectorType.ENDPOINT;
    }

    get isConstrainDisabled(): boolean {
        if (this.selectedElements?.length !== 1) return true;
        const element = this.resolveElement(this.selectedElements[0]);
        const elementType = element.getAttribute(ElementAttribute.iCreateType);
        if (elementType) return true;
        return false;
    }
}
